<template>
  <v-card>
    <v-card-title>
      {{ $t("tracks.import") }}
    </v-card-title>
    
    <v-progress-linear
      v-if="loading"
      indeterminate
      color="primary"
    ></v-progress-linear>

    <v-card-text>
      <div class="text-center" v-if="!imported && !loading" style="padding: 20px 0;">
        <input 
          type="file" 
          ref="fileInput" 
          style="display: none" 
          accept=".gpx,.tcx"
          @change="onFileChange"
        >
        <v-btn 
          color="primary" 
          @click="$refs.fileInput.click()" 
          x-large
          rounded
          block
        >
          <v-icon left>fa-file-upload</v-icon>
          {{ $t('elements.select_file') }}
        </v-btn>
      </div>
      
      <div v-if="imported && !loading" style="margin-top: 15px;">
        <v-text-field v-model="name" :label="$t('tracks.name')" prepend-icon="fa-font"></v-text-field>
        
        <v-layout row wrap>
          <v-flex xs12 sm6 style="padding: 5px;">
            <v-text-field 
              v-model="parsed_date" 
              type="date" 
              :label="$t('tracks.date')"
              prepend-icon="fa-calendar"
            ></v-text-field>
          </v-flex>
          <v-flex xs12 sm6 style="padding: 5px;">
            <v-text-field 
              v-model="parsed_time" 
              type="time" 
              :label="$t('tracks.time')"
              prepend-icon="fa-clock"
            ></v-text-field>
          </v-flex>
        </v-layout>

        <v-select
          v-model="selected_type"
          :items="track_types_list"
          item-text="name"
          item-value="value"
          :label="$t('tracks.type')"
          prepend-icon="fa-running"
        >
          <template v-slot:item="data">
            <i :class="'fad ' + data.item.icon" style="margin-right: 10px;"></i>
            {{ $t(data.item.name) }}
          </template>
          <template v-slot:selection="data">
            <i :class="'fad ' + data.item.icon" style="margin-right: 10px;"></i>
            {{ $t(data.item.name) }}
          </template>
        </v-select>

        <v-layout row wrap v-if="calc_distance">
          <v-flex xs6>
            <div style="color: grey; font-size: 12px;">{{ $t('tracks.distance') }}</div>
            <div>{{ Math.round(calc_distance / 10) / 100 }} km</div>
          </v-flex>
          <v-flex xs6 v-if="calc_heartrate">
            <div style="color: grey; font-size: 12px;">{{ $t('tracks.heartRateAvg') }}</div>
            <div>{{ Math.round(calc_heartrate) }} bpm</div>
          </v-flex>
        </v-layout>
      </div>
    </v-card-text>
    <v-card-actions>
      <v-spacer></v-spacer>
      <v-btn text @click="closeDialog" color="default">{{ $t("general.close") }}</v-btn>
      <v-btn text @click="saveData" color="primary" :disabled="!imported || loading" :loading="loading">{{ $t("tracks.import") }}</v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import { storage } from "../../schmucklicloud";
import { Notifications } from "../../notification";

import gpxParser from "../../plugins/gpx-parser";
import { parseTCXString } from "../../plugins/tcx-parser";
import track_types from "../../assets/data/types";

export default {
  data() {
    return {
      upload_data: null,
      import_data: null,

      loading: false,
      imported: false,
      
      name: "",
      type: null,
      track: null,
      
      parsed_date: "",
      parsed_time: "",
      selected_type: null,
      calc_distance: 0,
      calc_heartrate: 0,
    };
  },
  computed: {
    track_types_list() {
      return track_types.types;
    }
  },
  methods: {
    onFileChange(e) {
      if (e.target.files && e.target.files.length > 0) {
        this.upload_data = e.target.files[0];
        this.startImport();
      }
    },
    startImport() {
      this.imported = false;
      this.resetParsedData();
      
      if (this.upload_data !== undefined && this.upload_data !== null) {
        this.loading = true;
        //Get file type
        var file_name = this.upload_data.name.split(".");
        this.type = file_name[file_name.length - 1].toLowerCase();

        var reader = new FileReader();
        reader.readAsText(this.upload_data);
        reader.onload = function() {
          this.import_data = reader.result;
          this.parseData();
        }.bind(this);
      }
    },
    resetParsedData() {
      this.parsed_date = "";
      this.parsed_time = "";
      this.selected_type = null;
      this.calc_distance = 0;
      this.calc_heartrate = 0;
      this.name = "";
      this.track = null;
    },
    parseData() {
      try {
        switch (this.type) {
          case "gpx": {
            var gpx = new gpxParser();
            gpx.parse(this.import_data);
            this.track = gpx.tracks[0];
            this.name = this.track.name || gpx.metadata.name;
            
            this.calc_distance = this.track.distance.total;
            
            if (this.track.points && this.track.points.length > 0 && this.track.points[0].time) {
              let dt = new Date(this.track.points[0].time);
              this.setDateAndTime(dt);
            }
            
            let hr_sum_gpx = 0;
            let hr_count_gpx = 0;
            if (this.track.points) {
              this.track.points.forEach(p => {
                if (p.hr) {
                  hr_sum_gpx += p.hr;
                  hr_count_gpx++;
                }
              });
            }
            if (hr_count_gpx > 0) this.calc_heartrate = hr_sum_gpx / hr_count_gpx;
            
            this.suggestType(this.track.type);
            break;
          }
            
          case "tcx":
            parseTCXString(
              this.import_data,
              function(status, result) {
                this.track = result;
                this.name = this.track.title;
                
                this.calc_distance = this.track.distance;
                
                if (this.track.datetime) {
                  this.setDateAndTime(new Date(this.track.datetime));
                }
                
                let hr_sum_tcx = 0;
                let hr_count_tcx = 0;
                if (this.track.laps) {
                  this.track.laps.forEach(lap => {
                    if (lap.track) {
                      lap.track.forEach(p => {
                        if (p.hr) {
                          hr_sum_tcx += p.hr;
                          hr_count_tcx++;
                        }
                      });
                    }
                  });
                }
                if (hr_count_tcx > 0) this.calc_heartrate = hr_sum_tcx / hr_count_tcx;
                
                this.suggestType(this.track.type);
              }.bind(this)
            );
            break;
            
          default:
            this.resetView();
            Notifications.show(this.$t("tracks.notification.file_not_supported"));
            break;
        }
        this.imported = true;
      } catch (e) {
        console.error(e);
        Notifications.show(this.$t("tracks.notification.file_not_supported"));
      } finally {
        this.loading = false;
      }
    },
    setDateAndTime(dt) {
      if (!(dt instanceof Date && !isNaN(dt))) return;
      this.parsed_date = dt.getFullYear() + "-" + String(dt.getMonth() + 1).padStart(2, '0') + "-" + String(dt.getDate()).padStart(2, '0');
      this.parsed_time = String(dt.getHours()).padStart(2, '0') + ":" + String(dt.getMinutes()).padStart(2, '0');
    },
    suggestType(typeStr) {
      if (!typeStr) return;
      typeStr = typeStr.toLowerCase();
      if (typeStr.includes('bike') || typeStr.includes('biking') || typeStr.includes('cycling')) {
        this.selected_type = 4; // biking
      } else if (typeStr.includes('run')) {
        this.selected_type = 7; // running
      } else if (typeStr.includes('walk')) {
        this.selected_type = 2; // walking
      } else if (typeStr.includes('hik')) {
        this.selected_type = 3; // hiking
      } else if (typeStr.includes('ski')) {
        this.selected_type = 9; // skiing
      }
    },
    async saveData() {
      this.loading = true;
      
      let start_time_timestamp = null;
      if (this.parsed_date) {
        let datetimeStr = this.parsed_date;
        if (this.parsed_time) datetimeStr += "T" + this.parsed_time;
        let dt = new Date(datetimeStr);
        if (!isNaN(dt)) {
          start_time_timestamp = dt.getTime();
        }
      }
      
      var response = await storage.insert("tracks", {
        name: this.name,
        route: (this.import_data).replace(/\+/g, "%2B"), //Otherwise it would not save the plus sign
        type: this.type,
        start_time: start_time_timestamp,
        track_type: this.selected_type,
        calc_distance: this.calc_distance,
        calc_heartrate: this.calc_heartrate
      });

      if (response.isOK) {
        Notifications.show(
          this.$t("tracks.notification.track_has_been_imported")
        );
        this.$emit("importFinished");
        this.resetView();
        this.$router.push("/track/" + response.data.id);
      } else {
        Notifications.show(response.message);
      }
      this.loading = false;
    },
    closeDialog() {
      this.$emit("closeDialog");
    },
    resetView() {
      setTimeout(
        function() {
          this.import_data = null;
          this.upload_data = null;
          this.imported = false;
          this.loading = false;
          this.resetParsedData();
        }.bind(this),
        200
      );
    }
  }
};
</script>

<style>
</style>